// @ts-nocheck
/**
 * defaultValueValidation.ts — Validates CreationTimeDefault sentinel placement.
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue } from '../../diagnostics';
import { isCreationTimeDefault } from '../../types/defaults';
import { isTimestampItemFieldKey } from '../../types/defaults';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function validatePropertyDefaultValue(
  defaultValue: unknown,
  propertyType: string,
  path: string,
  result: ValidationResult,
): ValidationResult {
  if (!isCreationTimeDefault(defaultValue)) {
    return result;
  }
  if (propertyType !== 'date' && propertyType !== 'datetime') {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_default_value',
      message: `atCreation default is only allowed on date or datetime properties (got type '${propertyType}')`,
      path,
    });
  }
  return result;
}

export function validateCategoryPropertySeedValue(
  value: unknown,
  propertyType: string | undefined,
  path: string,
  result: ValidationResult,
): ValidationResult {
  if (!isCreationTimeDefault(value)) {
    return result;
  }
  if (propertyType !== 'date' && propertyType !== 'datetime') {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_default_value',
      message: `atCreation seed is only allowed on date or datetime category properties`,
      path,
    });
  }
  return result;
}

export function validateItemFieldSeedValue(
  key: string,
  value: unknown,
  path: string,
  result: ValidationResult,
): ValidationResult {
  if (!isCreationTimeDefault(value)) {
    return result;
  }
  if (!isTimestampItemFieldKey(key)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_default_value',
      message: `atCreation item field default is only allowed on timestamp fields (procuredAt, loanedAt)`,
      path,
    });
  }
  return result;
}

export function validateElementItemDefaults(
  itemDefaults: unknown,
  elementPath: string,
  propertyTypeByKey: Map<string, string> | ReadonlyMap<string, string>,
  result: ValidationResult,
): ValidationResult {
  if (!isPlainObject(itemDefaults)) {
    return result;
  }

  const itemFields = itemDefaults.itemFields;
  if (isPlainObject(itemFields)) {
    for (const [key, value] of Object.entries(itemFields)) {
      result = validateItemFieldSeedValue(
        key,
        value,
        `${elementPath}.itemDefaults.itemFields.${key}`,
        result,
      );
    }
  }

  const seeds = itemDefaults.categoryPropertyDefaults;
  if (Array.isArray(seeds)) {
    for (let i = 0; i < seeds.length; i++) {
      const seed = seeds[i];
      if (!isPlainObject(seed)) {
        continue;
      }
      const categoryId = typeof seed.categoryId === 'string' ? seed.categoryId : '';
      const propertyId = typeof seed.propertyId === 'string' ? seed.propertyId : '';
      const propType = propertyTypeByKey.get(`${categoryId}::${propertyId}`);
      result = validateCategoryPropertySeedValue(
        seed.value,
        propType,
        `${elementPath}.itemDefaults.categoryPropertyDefaults[${i}].value`,
        result,
      );
    }
  }

  return result;
}
